import React, { useState } from "react";
import axios from "axios";
import { Button, Divider, Form, Input, Modal, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { Success } from "../Messages/Messages";
import { generalErrorAlert } from "../GeneralErrorAlert";
import { isAuthenticated, logoutAndRedirect } from "./auth";

// Self-service account deletion (required by the App Store for apps with sign-in). What the
// server does depends on the role, see userController.deleteMyAccount: clients and employees
// lose their login at once, owners and admins file a request.
const explanation = role => {
  if (role === 0) {
    return "Your login and contact details are removed immediately. This cannot be undone. Invoices and offers addressed to you are kept as required by law.";
  }
  if (role === 1) {
    return "Your login is removed immediately. This cannot be undone. Your employer keeps the records it must retain by law, such as payroll.";
  }
  return "This sends a deletion request for your account and company to the LeistFusion team. We confirm with you before deleting anything. Records that must be kept by law, such as invoices and bookkeeping, are retained for the legal period.";
};

const DeleteAccount = () => {
  const { t } = useTranslation();
  const role = isAuthenticated()?.role;
  const deletesNow = role === 0 || role === 1;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const close = () => {
    setOpen(false);
    form.resetFields();
  };

  const onFinish = async ({ password }) => {
    setLoading(true);
    try {
      const res = await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/users/me`, {
        data: { password },
        headers: { Authorization: "Bearer " + localStorage.getItem("token") }
      });
      if (res.data?.status === "deleted") {
        Success(t("Your account was deleted."));
        logoutAndRedirect();
        return;
      }
      Success(t("Your deletion request was received. We will process it within 30 days."));
      close();
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 400 }}>
      <Divider />
      <Typography.Title level={5} type="danger">
        {t("Delete account")}
      </Typography.Title>
      <Typography.Paragraph type="secondary">{t(explanation(role))}</Typography.Paragraph>
      <Button danger size="large" onClick={() => setOpen(true)}>
        {deletesNow ? t("Delete my account") : t("Request account deletion")}
      </Button>
      <Modal
        open={open}
        title={t("Delete account")}
        onCancel={close}
        okText={deletesNow ? t("Delete my account") : t("Request account deletion")}
        okButtonProps={{ danger: true, loading }}
        onOk={() => form.submit()}
        destroyOnHidden
      >
        <Typography.Paragraph>{t(explanation(role))}</Typography.Paragraph>
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            label={t("Enter your password to confirm")}
            name="password"
            rules={[{ required: true, message: t("Please enter your password.") }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DeleteAccount;
